import React from "react";
import { Box } from "@mui/material";

export default function ContractHeader({ className = "", ...props }) {
  return (
    <Box
      className={className}
      {...props}
      sx={{
        width: "100%",
        overflow: "hidden",
        height: "300px",
        borderRadius: "4px",
        background: "rgb(240 244 248 / 0%)",
      }}
    >
      <Box
        component="svg"
        sx={{
          height: "100%",
          width: "100%",
        }}
        viewBox="0 0 900 220"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* =====================================================
              LEFT SIDE
              Upper-left -> center
              Soft / cloudy / layered
          ====================================================== */}

          <linearGradient id="leftWave1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c6dced" stopOpacity="0.72" />
            <stop offset="45%" stopColor="#d3e4f1" stopOpacity="0.46" />
            <stop offset="80%" stopColor="#e2edf6" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="leftWave2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#b8d2e7" stopOpacity="0.56" />
            <stop offset="42%" stopColor="#c9ddeb" stopOpacity="0.42" />
            <stop offset="78%" stopColor="#dce9f3" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="leftWave3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#d4e3ef" stopOpacity="0.58" />
            <stop offset="45%" stopColor="#dce9f3" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          {/* =====================================================
              RIGHT SIDE
              Right -> center
              More defined / tapered
          ====================================================== */}

          <linearGradient id="rightWave1" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#b9d3e8" stopOpacity="0.62" />
            <stop offset="45%" stopColor="#c8ddec" stopOpacity="0.42" />
            <stop offset="78%" stopColor="#dce9f3" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="rightWave2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#c9ddec" stopOpacity="0.52" />
            <stop offset="50%" stopColor="#d4e3ef" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="rightWave3" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#d6e5f0" stopOpacity="0.45" />
            <stop offset="55%" stopColor="#e0ebf4" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#edf3f8" stopOpacity="0" />
          </linearGradient>

          {/* Very subtle atmospheric blur */}

          <filter id="waveBlur" x="-10%" y="-30%" width="120%" height="160%">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
        </defs>

        {/* =====================================================
            LEFT WAVE 1
            Large upper-left cloud
        ====================================================== */}

        <path
          d="
            M 0 0
            L 520 0

            C 470 24, 420 48, 365 70
            C 305 94, 250 105, 195 101
            C 135 97, 78 75, 0 45

            Z
          "
          fill="url(#leftWave1)"
        />

        {/* =====================================================
            LEFT WAVE 2
            Main broad curved layer
        ====================================================== */}

        <path
          d="
            M 0 18

            C 75 45, 130 66, 190 76
            C 255 87, 315 78, 375 55
            C 430 34, 475 10, 510 -12

            L 575 0

            C 510 42, 452 76, 388 101
            C 320 127, 250 136, 182 124
            C 115 113, 61 87, 0 65

            Z
          "
          fill="url(#leftWave2)"
        />

        {/* =====================================================
            LEFT WAVE 3
            Lower overlapping cloud layer
        ====================================================== */}

        <path
          d="
            M 0 50

            C 65 75, 125 96, 190 103
            C 255 110, 317 98, 375 76
            C 430 55, 475 31, 515 4

            C 540 -12, 565 -20, 590 -22

            C 525 34, 474 77, 417 107
            C 350 143, 278 159, 205 151
            C 130 143, 65 111, 0 82

            Z
          "
          fill="url(#leftWave3)"
        />

        {/* =====================================================
            LEFT WAVE 4
            Very subtle lower contour
            Gives the "cloud" layering effect
        ====================================================== */}

        <path
          d="
            M 0 78

            C 75 104, 136 122, 202 128
            C 270 134, 333 119, 392 94
            C 445 72, 488 44, 525 18

            C 548 2, 565 -6, 580 -8

            C 525 48, 475 91, 420 119
            C 352 154, 276 173, 201 164
            C 127 155, 62 126, 0 104

            Z
          "
          fill="#dce9f3"
          opacity="0.20"
        />

        {/* =====================================================
            THIN LEFT CONTOUR
            Almost invisible, like the reference
        ====================================================== */}

        <path
          d="
            M 0 109
            C 75 130, 142 141, 210 139
            C 280 137, 342 118, 399 91
            C 447 68, 488 40, 526 12
          "
          fill="none"
          stroke="#c5dbe9"
          strokeWidth="1"
          opacity="0.34"
        />

        {/* =====================================================
            RIGHT WAVE 1
            Main wave entering from right
        ====================================================== */}

        <path
          d="
            M 900 42

            C 835 28, 775 30, 710 45
            C 635 62, 570 91, 510 119
            C 485 131, 460 140, 437 145

            C 462 147, 489 143, 516 135
            C 582 116, 651 92, 718 73
            C 783 55, 845 47, 900 54

            Z
          "
          fill="url(#rightWave1)"
        />

        {/* =====================================================
            RIGHT WAVE 2
            Slightly lower and narrower
        ====================================================== */}

        <path
          d="
            M 900 73

            C 842 61, 786 64, 727 78
            C 661 93, 601 115, 546 134
            C 514 145, 487 151, 463 153

            C 491 157, 519 154, 548 147
            C 605 133, 666 115, 728 101
            C 792 87, 849 83, 900 89

            Z
          "
          fill="url(#rightWave2)"
        />

        {/* =====================================================
            RIGHT WAVE 3
            Fine inner ribbon
        ====================================================== */}

        <path
          d="
            M 900 99

            C 850 90, 803 94, 753 104
            C 695 116, 641 132, 590 146
            C 548 157, 515 160, 487 158

            C 518 163, 551 162, 584 155
            C 636 145, 693 132, 752 121
            C 810 111, 858 110, 900 116

            Z
          "
          fill="url(#rightWave3)"
        />

        {/* =====================================================
            RIGHT WAVE 4
            Very thin finishing layer
        ====================================================== */}

        <path
          d="
            M 900 120

            C 853 113, 811 117, 766 125
            C 711 135, 661 146, 614 155
            C 571 163, 535 164, 505 160

            C 536 168, 573 168, 616 162
            C 667 155, 718 146, 769 139
            C 818 132, 861 132, 900 137

            Z
          "
          fill="#e1ebf4"
          opacity="0.30"
        />

        {/* =====================================================
            CENTRAL ENCOUNTER
            Soft overlap.
            NOT symmetrical.
        ====================================================== */}

        <path
          d="
            M 395 104
            C 420 99, 444 104, 466 115
            C 488 126, 507 133, 529 134
            C 509 143, 486 149, 463 150
            C 438 149, 416 141, 397 130
            Z
          "
          fill="#d0e1ee"
          opacity="0.20"
          filter="url(#waveBlur)"
        />

        {/* =====================================================
            CENTRAL SOFT LINE
            Helps blend the two systems
        ====================================================== */}

        <path
          d="
            M 350 124
            C 389 119, 424 121, 455 132
            C 478 140, 499 144, 520 142
          "
          fill="none"
          stroke="#d4e3ee"
          strokeWidth="2"
          opacity="0.18"
          filter="url(#waveBlur)"
        />

        {/* =====================================================
            VERY LIGHT TOP ATMOSPHERE
            Gives the header the same airy feeling
        ====================================================== */}

        <path
          d="
            M 0 4
            C 120 24, 230 28, 335 12
            C 410 1, 470 -10, 525 -25
          "
          fill="none"
          stroke="#dbe8f2"
          strokeWidth="3"
          opacity="0.20"
        />

        <path
          d="
            M 900 16
            C 820 5, 755 8, 690 25
            C 630 40, 575 64, 525 87
          "
          fill="none"
          stroke="#dce9f3"
          strokeWidth="2"
          opacity="0.20"
        />
      </Box>
    </Box>
  );
}
